/* eslint-disable max-len */
import {
    Form,
    Modal,
    Space,
    Typography,
    InputNumber,
    notification,
    Select,
    Card,
    Spin,
    Alert,
    Input,
} from 'antd';
import {
    FC, useState, useEffect, useRef,
} from 'react';
import { addAdjustmentTransactions } from '../../api/invoice-transactions';

interface IAdjustmentConcept {
    transactionID: string;
    currency: string;
    visible: boolean;
    total: number;
    onCancel: () => void;
    fetchConcepts: () => void;
}

const { Text } = Typography;
const { TextArea } = Input;

const AdjustmentConcept: FC<IAdjustmentConcept> = ({
    visible,
    onCancel,
    currency,
    total,
    transactionID,
    fetchConcepts,
}) => {
    const options = [
        {
            label: 'Entrega sin retorno',
            value: 'Pedido entregado al cliente, pero no retornado al comercio.',
        },
        {
            label: 'Pedido retornado',
            value: 'Pedido devuelto al punto de origen.',
        },
        {
            label: 'Cancelación',
            value: 'Conductor nunca llegó a recoger el pedido.',
        },
        {
            label: 'Reasignación por demora',
            value: 'Conductor demoró en llegar al punto de origen.',
        },
        {
            label: 'Llegada anticipada',
            value: 'Conductor registró la llegada antes de estar en el punto de origen.',
        },
        {
            label: 'Retraso en entrega del local',
            value: 'El local demoró en entregar la orden al conductor.',
        },
        {
            label: 'Pedido completado',
            value: 'Pedido retirado y entregado correctamente al cliente.',
        },
        {
            label: 'Liberación',
            value: 'Pedido no entregado al conductor por parte del local.',
        },
        {
            label: 'Otro',
            value: 'OTHER',
        },
    ];

    const [form] = Form.useForm<{
        amount: number;
        comment: string;
    }>();

    const [loading, setLoading] = useState<boolean>(false);
    const [amountValidation, setAmountValidation] = useState<{ loading: boolean, warning?: string, info?: string }>({ loading: false });
    const [comment, setComment] = useState<string>(options[0].value);
    const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false);
    const [customComment, setCustomComment] = useState<string>('');

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const amount = Form.useWatch('amount', form);

    const validateAmount = () => {
        setAmountValidation({ loading: true });
        const amount = form.getFieldValue('amount') as number;

        const result = Number(total) + Number(amount);

        const formatCurrency = (value: number) => {
            const absValue = Math.abs(value);
            const formatted = `${absValue.toFixed(2)}`;
            return value < 0 ? `- ${formatted}` : formatted;
        };

        const infoMessage = `${formatCurrency(total)} ${(amount > 0 ? '+' : '')} ${formatCurrency(amount)} = ${formatCurrency(result)} ${currency}.`;

        if (result < 0) {
            const warningMessage = 'The adjustment would leave the transaction total less than 0';
            setAmountValidation({ loading: false, warning: warningMessage, info: infoMessage });
            return;
        }

        setAmountValidation({ loading: false, info: infoMessage });
    };

    useEffect(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        if (amount !== undefined && amount !== null && amount !== 0) {
            debounceTimerRef.current = setTimeout(() => {
                validateAmount();
            }, 250);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, [amount]);

    useEffect(() => {
        if (visible) {
            setComment(options[0].value);
            setIsOtherSelected(false);
            setCustomComment('');
            setAmountValidation({ loading: false });
            form.resetFields();
        }
    }, [visible]);

    const handleOk = () => form.validateFields().then((values) => {
        setLoading(true);

        const payload = {
            ...values,
            comment: isOtherSelected ? customComment : comment,
        };

        addAdjustmentTransactions(transactionID, payload)
            .then(({ data }: any) => {
                notification.success({
                    message: 'Adjustment added successfully',
                });
                onCancel();
                fetchConcepts();
            })
            .catch((error) => {
                const description = error.response.data.message || 'Error adding adjustment';
                notification.error({
                    message: 'Error',
                    description,
                });
            })
            .finally(() => setLoading(false));
    });

    return (
        <Modal
            open={visible}
            onCancel={() => onCancel()}
            onOk={() => handleOk()}
            okButtonProps={{ disabled: loading || (isOtherSelected && !customComment.trim()) || Boolean(amountValidation.warning) }}
            okText="Add"
            confirmLoading={loading}
            title="Add Adjustment"
            centered
        >
            <Space direction="vertical" size={16}>
                <Text>
                    This adjustment will be added to the booking as a new
                    concept called Adjustment.
                </Text>
                {
                    amountValidation.loading && <Spin />
                }
                {
                    amountValidation.warning && (
                        <Alert message={amountValidation.warning} type="warning" />
                    )
                }
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        amount: 0,
                        comment: options[0].value,
                    }}
                >
                    <Form.Item
                        name="amount"
                        label="Amount"
                        rules={[
                            {
                                required: true,
                                message: 'Please enter an amount',
                            },
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            addonAfter={currency}
                            controls={false}
                            formatter={(value: any) => `$ ${value}`.replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ',',
                            )}
                        />
                    </Form.Item>
                    <Form.Item
                        name="comment"
                        label="Comment"
                        rules={[
                            {
                                required: true,
                                message: 'Please select a comment',
                            },
                        ]}
                    >
                        <Space direction="vertical" size={16} style={{ width: '100%' }}>
                            <Select
                                options={options}
                                placeholder="Select a comment"
                                value={isOtherSelected ? 'OTHER' : comment}
                                defaultValue={options[0].value}
                                onChange={(value) => {
                                    if (value === 'OTHER') {
                                        setIsOtherSelected(true);
                                        setComment('');
                                    } else {
                                        setIsOtherSelected(false);
                                        setComment(value);
                                    }
                                }}
                            />
                            {isOtherSelected ? (
                                <TextArea
                                    placeholder="Enter your custom comment..."
                                    value={customComment}
                                    onChange={(e) => setCustomComment(e.target.value)}
                                    rows={4}
                                    maxLength={500}
                                    showCount
                                />
                            ) : (
                                <Card style={{ width: '100%' }} bodyStyle={{ padding: '12px 16px' }}>
                                    <Space size={8} direction="vertical">
                                        <Text type="secondary">Message for the customer:</Text>
                                        <Text strong>{comment}</Text>
                                    </Space>
                                </Card>
                            )}
                        </Space>
                    </Form.Item>

                    <Card style={{ width: '100%', marginTop: 16 }} bodyStyle={{ padding: '12px 16px' }}>
                        <Space size={8} direction="vertical">
                            <Text type="secondary">Result:</Text>
                            {
                                amountValidation.loading ? <Spin /> : (
                                    <Text strong>{amountValidation.info}</Text>
                                )
                            }
                        </Space>
                    </Card>
                </Form>
            </Space>
        </Modal>
    );
};

export default AdjustmentConcept;
